// Logic thuần cho UI bối cảnh/thời đại (đợt 260804) — tách khỏi component để test được.
// (Import component vào test kéo theo antd v6, không load nổi dưới jest của react-scripts.)
import type { WorldSetting, ResolvedWorldSetting } from '../../types/film';

/**
 * ⚠️ Có HAI chữ "source" khác nghĩa nhau, đừng lẫn:
 *  • `WorldSetting.source`         = AI | MANUAL   → AI đoán hay người khai
 *  • `ResolvedWorldSetting.source` = PLAN | STORY  → bản nào đang có hiệu lực
 * Hàm dưới đây nói về cái THỨ HAI.
 */
type SettingOrigin = ResolvedWorldSetting['source'];

/** Field mang NỘI DUNG bối cảnh (`pinned`/`source` là cờ, không tính). */
const TEXT_FIELDS: (keyof WorldSetting)[] = ['era', 'world', 'architecture', 'costume'];

/**
 * Ô "từ cấm" (nhiều dòng) → mảng.
 * Chuẩn hoá phải KHỚP `normalizeWorldSetting` bên backend (trim + dedupe không phân biệt hoa/thường),
 * nếu không admin gõ "platinum, PLATINUM" sẽ thấy 2 dòng trên màn nhưng DB lưu 1 → tưởng mất dữ liệu.
 * Nhận cả xuống dòng lẫn dấu phẩy vì người ta gõ cả hai kiểu.
 */
export function parseForbiddenInput(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of (text || '').split(/[\n,]/)) {
    const t = raw.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Mảng → ô nhập, mỗi từ một dòng. */
export function formatForbiddenForInput(list?: string[] | null): string {
  return (list || []).join('\n');
}

export interface SourceDescription {
  label: string;
  color: string;
  /** Đang kế thừa từ truyện ⇒ cho phép tách ra khai riêng cho tập này. */
  canDetach: boolean;
}

/** Nhãn + màu cho tag nguồn, và biết nên hiện nút "khai riêng" hay "về kế thừa". */
export function describeSource(source: SettingOrigin | undefined): SourceDescription {
  if (source === 'PLAN') return { label: 'Episode override', color: 'purple', canDetach: false };
  if (source === 'STORY') return { label: 'Inherited from story', color: 'blue', canDetach: true };
  return { label: 'Not set', color: 'default', canDetach: false };
}

/** Có field nào mang nội dung không — quyết định hiện trạng thái rỗng thay vì form trống mênh mông. */
export function isEmptyWorldSetting(ws?: WorldSetting | null): boolean {
  if (!ws) return true;
  if (TEXT_FIELDS.some((f) => String(ws[f] ?? '').trim())) return false;
  return !(ws.forbidden || []).some((x) => String(x ?? '').trim());
}

/** Dạng so sánh được: trim chuỗi, giữ NGUYÊN thứ tự forbidden (thứ tự có nghĩa với người đọc). */
function comparable(ws?: WorldSetting | null): string {
  if (!ws) return '';
  const norm: Record<string, unknown> = {};
  for (const f of TEXT_FIELDS) {
    const v = String(ws[f] ?? '').trim();
    if (v) norm[f] = v;
  }
  const fb = (ws.forbidden || []).map((x) => String(x ?? '').trim()).filter(Boolean);
  if (fb.length) norm.forbidden = fb;
  if (ws.pinned) norm.pinned = true;
  // Không field nào có nội dung ⇒ coi như "chưa khai", để `null` và `{}` so ra bằng nhau
  // (mở form rồi đóng mà không gõ gì thì không được tính là có thay đổi cần lưu).
  return Object.keys(norm).length ? JSON.stringify(norm) : '';
}

/** Bản đang sửa có khác bản đã lưu không → bật/tắt nút Lưu. */
export function hasUnsavedChange(saved?: WorldSetting | null, draft?: WorldSetting | null): boolean {
  return comparable(saved) !== comparable(draft);
}

// ── E5-P2: trạng thái ảnh theo bối cảnh ──

/** Khớp `AssetEraStatus` của backend. */
export type AssetEraStatus = 'OK' | 'STALE' | 'UNKNOWN' | 'NO_SETTING';

export interface StaleItem {
  kind: 'location' | 'look' | 'character' | 'prop';
  id: string;
  name: string;
  /** Bối cảnh ghi trên bản ghi lúc gen (null = sinh trước đợt 260804). */
  assetEra: string | null;
  status: AssetEraStatus;
}

export interface StaleSummary {
  counts: { ok: number; stale: number; unknown: number };
  /** Chỉ mục cần người ngó tới. Giữ NGUYÊN thứ tự backend trả về. */
  visible: StaleItem[];
}

/**
 * Tổng hợp cho UI.
 * 🔴 `UNKNOWN` KHÔNG cộng vào `stale` — nó là "không rõ", không phải "cũ". Gộp lại sẽ đẩy admin gen lại
 * hàng loạt ảnh vốn có thể vẫn đúng (tốn tiền image-gen và làm mất niềm tin vào chính con số này).
 * `OK` chỉ đếm, không liệt kê — liệt kê cả thứ đang đúng chỉ làm nhiễu.
 */
export function summarizeStale(items?: StaleItem[] | null): StaleSummary {
  const list = items || [];
  return {
    counts: {
      ok: list.filter((i) => i.status === 'OK').length,
      stale: list.filter((i) => i.status === 'STALE').length,
      unknown: list.filter((i) => i.status === 'UNKNOWN').length,
    },
    visible: list.filter((i) => i.status === 'STALE' || i.status === 'UNKNOWN'),
  };
}

const ASSET_STATUS_META: Record<string, { label: string; color: string }> = {
  OK: { label: 'in period', color: 'green' },
  STALE: { label: 'outdated', color: 'warning' },
  UNKNOWN: { label: 'unknown', color: 'default' },
  NO_SETTING: { label: 'no period set', color: 'default' },
};

export function describeAssetStatus(status: AssetEraStatus): { label: string; color: string } {
  return ASSET_STATUS_META[status] || { label: String(status || '—'), color: 'default' };
}
